import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, ChevronUp, ChevronDown, Package } from 'lucide-react';
import useProducts from '../../hooks/useProducts';
import StatusBadge from '../../components/admin/StatusBadge';
import StockIndicator from '../../components/admin/StockIndicator';
import BulkActionBar from '../../components/admin/BulkActionBar';

export default function AdminDashboard() {
  const { getProducts, deleteProducts, bulkUpdateStatus } = useProducts();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [selected, setSelected] = useState([]);

  const products = useMemo(() =>
    getProducts({ search, status: statusFilter, category: categoryFilter, sortBy, sortDir }),
    [getProducts, search, statusFilter, categoryFilter, sortBy, sortDir]
  );

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('asc'); }
  };

  const toggleSelect = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAll = () => setSelected(prev => prev.length === products.length ? [] : products.map(p => p.id));

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
  };

  const selectStyle = { padding: '10px 14px', border: '1.5px solid #ede3d5', borderRadius: 10, fontSize: 13, fontFamily: 'inherit', background: '#fff', cursor: 'pointer' };
  const thStyle = { padding: '12px 14px', textAlign: 'left', fontWeight: 800, color: '#3d2b1f', cursor: 'pointer', userSelect: 'none' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, color: '#3d2b1f', fontStyle: 'italic' }}>Products</h1>
          <p style={{ fontSize: 14, color: '#7a5c4a', marginTop: 4 }}>{products.length} product{products.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="admin-btn admin-btn-primary" onClick={() => navigate('/admin/products/new')}>
          <Plus size={15} /> Add product
        </button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#7a5c4a' }} />
          <input type="text" placeholder="Search products..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '100%', padding: '10px 10px 10px 36px', border: '1.5px solid #ede3d5', borderRadius: 10, fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={selectStyle}>
          <option value="all">All status</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          <option value="archived">Archived</option>
        </select>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} style={selectStyle}>
          <option value="all">All categories</option>
          <option value="Girls">Girls</option>
          <option value="Boys">Boys</option>
          <option value="Baby">Baby</option>
        </select>
      </div>

      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #ede3d5', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #ede3d5', background: '#faf6f0' }}>
              <th style={{ padding: '12px 14px', textAlign: 'left', width: 40 }}>
                <input type="checkbox" checked={selected.length === products.length && products.length > 0}
                  onChange={toggleAll} style={{ cursor: 'pointer' }} />
              </th>
              <th style={{ padding: '12px 0', width: 50 }}></th>
              <th style={thStyle} onClick={() => handleSort('name')}>Product <SortIcon field="name" /></th>
              <th style={thStyle} onClick={() => handleSort('status')}>Status <SortIcon field="status" /></th>
              <th style={thStyle} onClick={() => handleSort('stock')}>Inventory <SortIcon field="stock" /></th>
              <th style={thStyle} onClick={() => handleSort('category')}>Category <SortIcon field="category" /></th>
              <th style={{ ...thStyle, textAlign: 'right' }} onClick={() => handleSort('price')}>Price <SortIcon field="price" /></th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 48, textAlign: 'center', color: '#7a5c4a' }}>
                <Package size={32} style={{ marginBottom: 8, opacity: 0.4 }} /><br />No products found
              </td></tr>
            ) : products.map(p => (
              <tr key={p.id} style={{ borderBottom: '1px solid #f5efe4', cursor: 'pointer', transition: 'background .15s' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#faf6f0'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                onClick={() => navigate(`/admin/products/${p.id}`)}>
                <td style={{ padding: '10px 14px' }} onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.includes(p.id)}
                    onChange={() => toggleSelect(p.id)} style={{ cursor: 'pointer' }} />
                </td>
                <td style={{ padding: '10px 0' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 8, overflow: 'hidden', background: p.bg || '#f5efe4' }}>
                    {p.img ? <img src={p.img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>{p.emoji}</div>}
                  </div>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <div style={{ fontWeight: 700, color: '#3d2b1f' }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: '#7a5c4a', marginTop: 2 }}>{p.subtitle}</div>
                </td>
                <td style={{ padding: '10px 14px' }}><StatusBadge status={p.status} /></td>
                <td style={{ padding: '10px 14px' }}><StockIndicator stock={p.stock} trackInventory={p.trackInventory} /></td>
                <td style={{ padding: '10px 14px', color: '#7a5c4a' }}>{p.category}</td>
                <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 800, color: '#c0735a' }}>₹{p.price}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected.length > 0 && (
        <BulkActionBar
          count={selected.length}
          onSetActive={() => { bulkUpdateStatus(selected, 'active'); setSelected([]); }}
          onSetDraft={() => { bulkUpdateStatus(selected, 'draft'); setSelected([]); }}
          onArchive={() => { bulkUpdateStatus(selected, 'archived'); setSelected([]); }}
          onDelete={() => { if (window.confirm(`Delete ${selected.length} product(s)? This cannot be undone.`)) { deleteProducts(selected); setSelected([]); } }}
          onDeselect={() => setSelected([])}
        />
      )}
    </div>
  );
}
